import { Observable, from, isObservable, of } from 'rxjs';
import { isEmpty, isObject, isPromise, isArray } from './assertion.utils';

/**
 * @description
 * coerce (force) a typed value to be an observable of that type, if the value is
 * already an observable - then we do nothing
 * @param value a value of any type (observable or not)
 * @returns an observable of type T
 */
export function coerceObservable<T>(value: T | Observable<T>): Observable<T> {
  if (isObservable(value)) {
    return value as Observable<T>;
  }
  if (Array.isArray(value) || isPromise<T>(value)) {
    return from(value);
  }
  return of(value);
}

/**
 * @description
 * convert a value of any type to an array. if value is already an array - do nothing,
 * if value is not an array - create a new array and return it as the first item in that
 * array
 * @param value a value of any type
 * @returns an array of same type as value, with value as its first item (array[0] = item)
 */
export function coerceArray<T>(value: T | T[]): T[] {
  return Array.isArray(value) ? value : [value];
}

export function coerceBoolean<T>(value: T): boolean {
  if (isEmpty(value)) {
    return false;
  }
  return Boolean(!!value);
}

/**
 * @description
 * force any value to be a number, whenever casting is possible. if value conversion
 * into numeric value is possible - return that converted value as number, if conversion
 * is not possible = return undefined. special handler for string value, in case it is
 * a formatted numeric string ("5,236.2") in which case, we clean the string from any
 * non-numeric chars and return just the numeric value;
 * @param value any value type
 * @returns a numeric value of the original value, or undefined when numeric conversion
 * not possible
 */
export function coerceNumber<T>(value: T): number {
  const strValue = typeof value === 'string' ? value.replace(/[^0-9.-]+/g, '') : value;
  const numValue = +strValue;

  if (isNaN(numValue)) {
    return undefined;
  }

  return numValue;
}

export function coerceDictionary<T>(value: T): T {
  if (isObject(value)) {
    return value as T;
  }
  return {} as T;
}

export function coerceString(value: any): string {
  if (isEmpty(value) || isObject(value) || isArray(value)) {
    value = '';
  }

  return (value as string).toString();
}
